'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { type ReactElement, useRef } from 'react';

import { Button } from '@/app/components/client/button';
import { StorytellingBase } from '@/app/components/client/storytelling-base';
import { CmsImage } from '@/app/components/server/cms-image';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { type PdpParamsParsed } from '../../page';
import { type Header } from '../header';
import { HeaderForm, type HeaderFormProps } from './header-form';

type Props = {
  data: Header & { ironform: HeaderFormProps | null };
  params: PdpParamsParsed;
} & BoxProps;

export const HeaderWithForm = (props: Props): ReactElement | null => {
  const {
    as = 'header',
    data: {
      button: [button],
      description,
      image,
      tag,
      title,
      ironform,
    },
    params,
    ...boxProps
  } = props;

  const formRef = useRef<HTMLDivElement>(null);
  const { isEnabled } = useFeatureToggle();

  if (!isEnabled('pdp-hero-form')) return null;

  const scrollToFormTop = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <ModuleWrapper
      innerProps={{
        align: 'start',
        direction: ['column', null, 'row'],
        gap: 3,
      }}
      moduleFormat={{
        background: 'transparent',
        spacingBottom: ['16', null, '40'],
        spacingTop: ['24', null, '40'],
      }}
      outerProps={{ as, ...boxProps }}
    >
      <Flex
        direction="column"
        maxW={[null, null, '531px']}
        w={['100%', null, '50.5%']}
      >
        {tag && <StorytellingBase.Tag text={tag} />}

        <Flex direction="column" gap={2}>
          <Text as="h1" textStyle={['3xl', null, '5xl']}>
            {title}
          </Text>
          <CmsImage data={image.responsiveImage} />
          <StorytellingBase.Card content={description} />
        </Flex>

        {button && (
          <Button
            maxW="full"
            mt={[2, null, 4]}
            tracking={{ f: 'pd' }}
            {...button}
          />
        )}
      </Flex>

      {ironform && (
        <HeaderForm
          {...ironform}
          background="background.white"
          borderRadius="xl"
          maxW={[null, null, '520px']}
          onSubmit={scrollToFormTop}
          params={params}
          pt={3}
          ref={formRef}
          w={['100%', null, '49.5%']}
        />
      )}
    </ModuleWrapper>
  );
};
